<template>
  <div class="stamping-status-container">
    <separator-extended
      :text="$t('stampingStatus')"
      class="separator__extended"
    />

    <div v-if="loading" class="text-center p-4">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
    </div>

    <div v-else-if="documentsWithStamping.length > 0" class="px-3 pt-2">
      <!-- Botón Reintentar Todos -->
      <div v-if="canRetryAll" class="mb-3">
        <action-button
          class="outline-danger px-4 py-2"
          i="fa fa-redo"
          :text="$t('retryAll')"
          @click="retryAll"
          :disabled="processing"
        />
      </div>

      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('document') }}</th>
              <th>{{ $t('category') }}</th>
              <th>{{ $t('stampingStatus') }}</th>
              <th>{{ $t('stampingDate') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="doc in documentsWithStamping"
              :key="doc.itemAttachmentId"
            >
              <td>
                <div class="d-flex align-items-center">
                  <i class="far fa-file-pdf mr-2 text-danger"></i>
                  <span class="font-14">{{ doc.fileName }}</span>
                </div>
              </td>
              <td>{{ doc.attachmentCategoryName }}</td>
              <td>
                <span
                  class="badge"
                  :class="{
                    'badge-warning':
                      doc.stampingStatus === 'Pendiente' &&
                      !doc.processingError,
                    'badge-success': doc.stampingStatus === 'Ponchado',
                    'badge-info': doc.stampingStatus === 'Procesando',
                    'badge-danger': doc.processingError,
                  }"
                >
                  <span v-if="doc.processingError">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ $t('error') }}
                  </span>
                  <span v-else>
                    {{ doc.stampingStatus }}
                  </span>
                </span>
                <span v-if="doc.processingError" class="ml-1">
                  <i
                    class="fas fa-info-circle text-danger cursor-pointer"
                    :title="doc.processingError"
                    v-b-tooltip.hover
                  ></i>
                </span>
              </td>
              <td>
                <span v-if="doc.stampingDate">
                  {{ formatDate(doc.stampingDate) }}
                </span>
                <span v-else class="text-muted">--</span>
              </td>
              <td>
                <div class="btn-group" role="group">
                  <action-button
                    class="outline-primary px-2 py-1 mr-1"
                    i="fa fa-download"
                    :title="$t('download')"
                    @click="onDownloadHandler(doc.itemAttachmentId)"
                  />

                  <action-button
                    v-if="canRetryStamping(doc)"
                    class="outline-danger px-2 py-1 mr-1"
                    i="fa fa-redo"
                    :title="$t('retryStamping')"
                    @click="retryStamping(doc)"
                    :disabled="processing"
                  />

                  <span
                    v-if="doc.processingAttempts > 0"
                    class="badge badge-warning ml-2"
                  >
                    {{ $t('attempts') }}: {{ doc.processingAttempts }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="stamping-summary mt-3 p-3 bg-light rounded">
        <div class="row">
          <div class="col-md-4 text-center">
            <div class="stat-box">
              <h4 class="mb-0 text-primary">{{ totalDocuments }}</h4>
              <small class="text-muted">{{ $t('totalDocuments') }}</small>
            </div>
          </div>
          <div class="col-md-4 text-center">
            <div class="stat-box">
              <h4 class="mb-0 text-success">{{ stampedCount }}</h4>
              <small class="text-muted">{{ $t('stamped') }}</small>
            </div>
          </div>
          <div class="col-md-4 text-center">
            <div class="stat-box">
              <h4 class="mb-0 text-warning">{{ pendingCount }}</h4>
              <small class="text-muted">{{ $t('pending') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center p-4 text-muted">
      <i class="far fa-folder-open fa-3x mb-3"></i>
      <p>{{ $t('noDocumentsToStamp') }}</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ActionButton from 'ActionButton';
import { SeparatorExtended } from '@/components/common';
import {
  getAttachmentsWithStamping,
  downloadItemAttachment,
  executeManualStamping,
} from '@/services/app.api';
import alertMixin from 'utils/mixins/alert.mixin';
import { utilityMixin } from '@/mixins/utility.mixin';
import { name as userModuleName } from '@/store/modules/user';
import moment from 'moment';

export default {
  name: 'StampingStatus',
  mixins: [alertMixin, utilityMixin],
  components: {
    ActionButton,
    SeparatorExtended,
  },
  data() {
    return {
      loading: true,
      documentsWithStamping: [],
      processing: false,
    };
  },
  computed: {
    ...mapState('user', ['currentUser']),
    ...mapGetters(userModuleName, {
      userRole: 'role',
    }),

    businessId() {
      return this.itemModel?.businessId || this.itemModel?.referenceNumber;
    },

    itemStatus() {
      return this.itemModel?.requestStatusId;
    },

    canManageStamping() {
      const role =
        this.userRole || this.roleName?.() || this.currentUser?.role || '';
      return ['SUPERVISOR', 'ADMIN', 'ADMINISTRADOR'].includes(
        role.toUpperCase()
      );
    },

    isAuthorized() {
      return this.itemStatus === 24;
    },

    isRedaccionCartaOrTesis() {
      return this.itemStatus === 23 || this.itemStatus === 27;
    },

    totalDocuments() {
      return this.documentsWithStamping.length;
    },

    stampedCount() {
      return this.documentsWithStamping.filter(
        (d) => d.stampingStatus === 'Ponchado'
      ).length;
    },

    pendingCount() {
      return this.documentsWithStamping.filter(
        (d) => d.stampingStatus === 'Pendiente'
      ).length;
    },

    canRetryAll() {
      // Mostrar botón "Reintentar Todos" si:
      // 1. Usuario es SUPERVISOR/ADMIN
      // 2. Solicitud está Autorizada (24)
      // 3. Hay al menos un documento Pendiente o con error
      if (!this.canManageStamping || !this.isAuthorized) return false;

      return this.documentsWithStamping.some(
        (d) => d.stampingStatus === 'Pendiente' || d.processingError
      );
    },
  },
  async mounted() {
    this.$nextTick(async () => {
      if (this.businessId && this.isAuthorized) {
        await this.loadDocumentsWithStamping();
      } else {
        this.loading = false;
      }
    });
  },
  watch: {
    'itemModel.businessId': {
      handler(newVal) {
        if (newVal && this.isAuthorized) {
          this.loadDocumentsWithStamping();
        }
      },
      immediate: false,
    },
    'itemModel.requestStatusId': {
      handler(newVal) {
        if (newVal === 24 && this.businessId) {
          this.loadDocumentsWithStamping();
        }
      },
      immediate: false,
    },
  },
  methods: {
    async loadDocumentsWithStamping() {
      try {
        this.loading = true;
        this.documentsWithStamping = await getAttachmentsWithStamping(
          this.businessId
        );
      } catch (error) {
        console.error('Error loading documents with stamping:', error);
        this.ShowToast('', this.$t('errorLoadingStampingStatus'), 'error');
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    },

    async onDownloadHandler(itemAttachmentId) {
      try {
        await downloadItemAttachment(itemAttachmentId);
      } catch (error) {
        console.error('Error downloading document:', error);
        this.ShowToast('', this.$t('errorDownloadingDocument'), 'error');
      }
    },

    canRetryStamping(doc) {
      const isPendingOrError =
        doc.stampingStatus === 'Pendiente' || doc.processingError;

      return (
        this.canManageStamping &&
        this.isAuthorized &&
        !this.isRedaccionCartaOrTesis &&
        isPendingOrError
      );
    },

    async retryStamping(doc) {
      const confirm = await this.ShowCancelModal(
        this.$t('retryStamping'),
        `${this.$t('confirmRetry')}\n\n${this.$t('document')}: ${
          doc.fileName
        }\n${this.$t('attempts')}: ${doc.processingAttempts || 0}`,
        this.$t('yes'),
        this.$t('no')
      );

      if (!confirm) return;

      try {
        this.processing = true;

        await executeManualStamping(doc.itemAttachmentId);

        this.ShowToast(
          this.$t('success'),
          this.$t('stampingQueued'),
          'success'
        );

        setTimeout(async () => {
          await this.loadDocumentsWithStamping();
        }, 3000);
      } catch (error) {
        console.error('Error retrying stamping:', error);
        const errorMessage =
          error.response?.data?.error || this.$t('errorRetrying');
        this.ShowToast('', errorMessage, 'error');
      } finally {
        this.processing = false;
      }
    },

    async retryAll() {
      const pendingDocs = this.documentsWithStamping.filter(
        (d) => d.stampingStatus === 'Pendiente' || d.processingError
      );

      const confirm = await this.ShowCancelModal(
        this.$t('retryAll'),
        `${this.$t('confirmRetryAll')}\n\n${this.$t('totalDocuments')}: ${
          pendingDocs.length
        }`,
        this.$t('yes'),
        this.$t('no')
      );

      if (!confirm) return;

      try {
        this.processing = true;
        let successCount = 0;
        let errorCount = 0;

        for (const doc of pendingDocs) {
          try {
            await executeManualStamping(doc.itemAttachmentId);
            successCount++;
          } catch (error) {
            console.error(
              `Error retrying stamping for ${doc.fileName}:`,
              error
            );
            errorCount++;
          }
        }

        this.ShowToast(
          this.$t('success'),
          `${this.$t(
            'stampingQueued'
          )} - Éxitos: ${successCount}, Errores: ${errorCount}`,
          errorCount > 0 ? 'warning' : 'success'
        );

        setTimeout(async () => {
          await this.loadDocumentsWithStamping();
        }, 3000);
      } catch (error) {
        console.error('Error in retryAll:', error);
        this.ShowToast('', this.$t('errorRetrying'), 'error');
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style scoped>
.stamping-status-container {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table {
  font-size: 14px;
}

.badge {
  font-size: 12px;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
}

.stamping-summary {
  border-top: 1px solid #dee2e6;
}

.stat-box {
  padding: 1rem;
}

.stat-box h4 {
  font-size: 1.75rem;
  font-weight: 600;
}

.stat-box small {
  font-size: 0.875rem;
}

.cursor-pointer {
  cursor: pointer;
}

.separator__extended {
  margin-bottom: 1rem;
}
</style>
